import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Select, SegmentedTabs, Skeleton } from "@akuna/ui";
import { ChevronRight } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import {
  useCatalogGroups, useCatalogItems, useCommissionModels, useMemberCommission, useSetItemOverride, useSetMemberModel, useSetOverride,
} from "@/hooks/useCommissions";
import { formatCurrency, formatPercent } from "@/lib/format/br";
import { commissionErrorKey } from "@/lib/commissions/api";
import type { GroupKind } from "@/lib/commissions/groups";
import { buildMemberTree, type MemberGroupNode } from "@/lib/commissions/tree";
import type { TeamMemberDetail } from "@/lib/team/types";
import { cn } from "@/lib/utils";
import { PercentCell } from "@/components/commissions/PercentCell";

const card = "flex flex-col rounded-card border border-border bg-card";
const firstName = (full: string) => full.split(" ")[0];
const KINDS: GroupKind[] = ["service", "product", "package"];
const COLS = "grid grid-cols-[minmax(0,1fr)_4.5rem_auto] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_6rem_13rem]";

export function MemberCommissionSection({ member }: { member: TeamMemberDetail }) {
  const { t, i18n } = useTranslation("commissions");
  const { hasPermission: canView } = useHasPermissionSync("commissions", "view");
  const { hasPermission: canUpdate } = useHasPermissionSync("commissions", "edit");
  const allowed = canView || member.is_self;
  // Own commission is editable with the permission too; only the salary keeps the self-lock.
  const canEdit = canUpdate;
  const { data: setup, isLoading } = useMemberCommission(member.user_id, allowed);
  // Only the editors need the full catalog; the read-only view uses what the setup mentions.
  const { data: models = [] } = useCommissionModels(canEdit);
  const { data: groups = [] } = useCatalogGroups(canEdit);
  const { data: items = [] } = useCatalogItems(canEdit);
  const setModel = useSetMemberModel();
  const setOverride = useSetOverride();
  const setItemOverride = useSetItemOverride();
  const [kind, setKind] = useState<GroupKind>("service");
  const [open, setOpen] = useState<Set<string>>(new Set());

  const tree = useMemo(() => {
    if (!setup) return [];
    const catalog = canEdit
      ? { groups: groups.filter((g) => g.kind === kind), items: items.filter((i) => i.kind === kind) }
      : null;
    return buildMemberTree(kind, setup, catalog, i18n.language);
  }, [setup, canEdit, groups, items, kind, i18n.language]);
  const exceptions = useMemo(() => {
    if (!setup) return 0;
    return setup.overrides.length + setup.item_overrides.length;
  }, [setup]);

  if (!allowed) return null;
  if (isLoading || !setup) return <Skeleton label={t("member.title")} className="h-64 w-full" />;

  const run = async (p: Promise<void>) => {
    try {
      await p;
      toast({ title: t("member.saved") });
    } catch (error) {
      toast({ title: t(commissionErrorKey(error)), variant: "destructive" });
    }
  };
  const name = firstName(member.full_name);
  const pct = (v: number | null) => (v === null ? t("catalog.models.noRate") : formatPercent(v, i18n.language));
  const keyOf = (n: MemberGroupNode) => n.groupId ?? "none";
  const toggle = (key: string) =>
    setOpen((s) => {
      const copy = new Set(s);
      if (copy.has(key)) copy.delete(key);
      else copy.add(key);
      return copy;
    });

  return (
    <div className="flex flex-wrap items-start gap-6">
      <section data-section="commission" className={`${card} min-w-0 flex-[999_1_600px] overflow-hidden`} aria-labelledby="member-commission">
        <div className="flex flex-col gap-4 border-b border-border p-6">
          <div className="flex flex-col gap-1">
            <h2 id="member-commission" className="text-base font-semibold">{t("member.title")}</h2>
            <p className="text-sm text-muted-foreground">{t("member.description", { name })}</p>
          </div>
          {canEdit ? (
            <div className="flex flex-wrap items-end gap-3">
              <Select
                forceNative
                className="flex-[1_1_260px]"
                label={t("member.model")}
                value={setup.model_id ?? ""}
                onValueChange={(v) => run(setModel.mutateAsync({ userId: member.user_id, modelId: v || null }))}
                options={[{ value: "", label: t("member.noModel") }, ...models.filter((m) => m.is_active || m.id === setup.model_id).map((m) => ({ value: m.id, label: m.name }))]}
              />
              <Button asChild variant="outline"><Link to="/catalog/commissions">{t("member.editModels")}</Link></Button>
            </div>
          ) : (
            <p className="text-sm"><span className="text-muted-foreground">{t("member.model")}: </span>{setup.model_name ?? t("member.noModel")}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-5">
          <SegmentedTabs
            variant="outline"
            label={t("catalog.models.kindLabel")}
            value={kind}
            onValueChange={(v) => {
              setKind(v as GroupKind);
              setOpen(new Set());
            }}
            items={KINDS.map((k) => ({ value: k, label: t(`kinds.${k}`) }))}
          />
          <span className="text-sm text-muted-foreground">
            {exceptions === 0 ? t("member.noExceptions") : t("member.exceptions", { count: exceptions, name })}
          </span>
        </div>

        {tree.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-muted-foreground">{t("member.noRates")}</p>
        ) : (
          <>
            <div className={cn(COLS, "bg-muted px-4 py-2.5 text-xs font-medium text-muted-foreground sm:px-5")}>
              <span>{t("catalog.models.groupOrItem")}</span>
              <span className="text-right">{t("member.inModel")}</span>
              <span className="text-right">{t("member.forPerson", { name })}</span>
            </div>
            {tree.map((node) => {
              const key = keyOf(node);
              const isOpen = open.has(key);
              const groupName = node.groupId ? node.name : t("catalog.models.noGroup");
              const panelId = `member-commission-${kind}-${key}`;
              const groupEffective = node.override ?? node.modelRate;
              return (
                <div key={key} className="border-t border-border">
                  <div className={cn(COLS, "px-2 py-1.5 sm:px-3")}>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggle(key)}
                      className="flex min-h-12 min-w-0 items-center gap-2.5 rounded-sm px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <ChevronRight
                        className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none", isOpen && "rotate-90")}
                        aria-hidden="true"
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-semibold">{groupName}</span>
                        <span className="text-xs text-muted-foreground">
                          {t("catalog.models.itemCount", { count: node.items.length })}
                          {node.exceptionCount > 0 && ` · ${t("member.groupExceptions", { count: node.exceptionCount })}`}
                        </span>
                      </span>
                    </button>
                    <span className="text-right text-sm tabular-nums text-muted-foreground">{node.groupId ? pct(node.modelRate) : ""}</span>
                    <div className="flex items-center justify-end gap-2">
                      {!node.groupId ? (
                        <span className="text-xs text-muted-foreground">{t("catalog.models.itemByItem")}</span>
                      ) : canEdit ? (
                        <>
                          {node.override !== null && (
                            <Button variant="link" size="sm" className="min-h-11" onClick={() => run(setOverride.mutateAsync({ userId: member.user_id, kind, groupId: node.groupId!, percent: null }))}>
                              {t("member.back")}
                            </Button>
                          )}
                          <PercentCell
                            label={t("member.percentLabel", { group: node.name, name: member.full_name })}
                            placeholder="0"
                            highlighted={node.override !== null}
                            value={groupEffective}
                            onCommit={(percent) =>
                              run(setOverride.mutateAsync({
                                userId: member.user_id, kind, groupId: node.groupId!,
                                percent: percent === node.modelRate ? null : percent,
                              }))
                            }
                          />
                        </>
                      ) : (
                        <span className="tabular-nums">{pct(groupEffective)}</span>
                      )}
                    </div>
                  </div>
                  {isOpen && (
                    <ul id={panelId} className="pb-2">
                      {node.items.map((item) => (
                        <li key={item.id} className={cn(COLS, "border-t border-dashed border-border py-1.5 pl-10 pr-4 sm:pl-14 sm:pr-5")}>
                          <span className="flex min-w-0 items-center gap-2 text-sm">
                            <span className="truncate">{item.name}</span>
                            {item.override !== null && (
                              <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-accent-foreground">
                                {t("member.onlyFor", { name })}
                              </span>
                            )}
                          </span>
                          <span className="text-right text-sm tabular-nums text-muted-foreground">{pct(item.modelRate)}</span>
                          <div className="flex items-center justify-end gap-2">
                            {canEdit ? (
                              <>
                                {item.override !== null && (
                                  <Button variant="link" size="sm" className="min-h-11" onClick={() => run(setItemOverride.mutateAsync({ userId: member.user_id, itemId: item.id, percent: null }))}>
                                    {t("member.back")}
                                  </Button>
                                )}
                                <PercentCell
                                  label={t("member.itemPercentLabel", { item: item.name, name: member.full_name })}
                                  placeholder="0"
                                  highlighted={item.override !== null}
                                  value={item.effective}
                                  onCommit={(percent) => {
                                    // Without the item exception the item would fall back to this value: no exception needed.
                                    const fallback = node.override ?? item.modelRate;
                                    run(setItemOverride.mutateAsync({ userId: member.user_id, itemId: item.id, percent: percent === fallback ? null : percent }));
                                  }}
                                />
                              </>
                            ) : (
                              <span className="tabular-nums">{pct(item.effective)}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </>
        )}
        <p className="border-t border-border bg-muted px-6 py-3 text-xs text-muted-foreground">
          {t("basis.label")}: {t(`basis.${setup.calculation_basis}`)}
          {!canEdit && ` · ${t("member.readOnly")}`}
        </p>
      </section>

      <aside className={`${card} min-w-0 flex-[1_1_320px] gap-5 p-6`} aria-labelledby="member-month">
        <h2 id="member-month" className="text-base font-semibold">{t("member.monthTitle")}</h2>
        {setup.month.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("member.monthEmpty")}</p>
        ) : (
          <ul className="flex flex-col gap-2.5 text-sm">
            {setup.month.map((m) => (
              <li key={m.group_name} className="flex justify-between gap-3">
                <span>{m.group_name || t("catalog.models.noGroup")} <span className="text-muted-foreground">· {formatCurrency(m.base, i18n.language)}</span></span>
                <span className="tabular-nums">{formatCurrency(m.amount, i18n.language)}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-baseline justify-between gap-2 rounded-md bg-secondary p-4">
          <span className="text-sm font-medium">{t("member.monthTotal")}</span>
          <span className="text-xl font-bold tabular-nums">{formatCurrency(setup.month_total, i18n.language)}</span>
        </div>
      </aside>
    </div>
  );
}
